/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { MenuItem } from "@mui/material";
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox";
import ArgosSelect from "../../../../../components/Form/Select/ArgosSelect";
import { MRT_Cell, MRT_ColumnDef, MaterialReactTable } from "material-react-table";
import { ObjectPropertiesInput, ObjectPropertyRow, objectPropertiesInput } from "../../constants";
import {useEffect, useMemo, useState} from "react";
import { toast } from "react-hot-toast";
import { useHasWriteAccess } from "../../../../../../redux/ducks/AccessControl/accessControlHelpers";
import { ArgosTable } from "../../../../../components/Table/ArgosTable";
import {ArgosTableSimple} from "../../../../../components/Table/ArgosTableSimple";


interface IObjectPropertiesProps {
  baselineData: any
  isDialogOpen: any
  gmid: string
  configIndex: any
  setIsPublicConfig: any
  newDataList: any
  setSelectedPointsFrames: any,
  temperatureArray: number[],
  handleTempChange: any

  handleObjectCategoryChange: any
  objectData: any
  objectDataMap: any
  handleObjectNameChange: any
  processListOfVersions: any
  handleObjectVersionChange: any
  handleProfileChange: any

  handleDimensionChange: any

    calcHorizontalSize: any
    calcVerticalSize: any

    calcPixelCount: any

  calcPointsOnTarget: any
}
const ObjectProperties = (props: IObjectPropertiesProps) => {
  const [tableData, setTableData] = useState<any>([]);
  const dataList = props?.newDataList
  const [selectedName, setSelectedName] = useState<string>(props?.baselineData?.selectedObject);
  const [selectedPointsFrame, setSelectedPointsFrame] = useState<string>(props?.baselineData?.selectedPointsFrames)

  const dimensionOptions = ["Short", "Long"]

  useEffect(() => {
    populateTable();
    setSelectedPointsFrame(props?.baselineData?.selectedPointsFrames)
    setSelectedName(props?.baselineData?.selectedObject)
  }, [props])

  const populateTable = () => {
    let _tableData: ObjectPropertyRow[] = []
    let data = props?.baselineData;
    objectPropertiesInput.forEach((element: ObjectPropertiesInput) => {
      let _tableDataObject: ObjectPropertyRow = {
        objectPropertyName: element.label,
        objectPropertyValue: data?.[element.key],
        objectPropertyKey: element.key,
      }
      _tableData.push(_tableDataObject)
    })
    setTableData(_tableData)
  }

  const [signVariants, setSignVariants] = useState<any>([]);
  const [signVariantsMapping, setSignVariantsToDataMapping] = useState<any>({});

  /**
   * Constructs a list of variant dimensions for a specific sign object based on its name, version, and category.
   *
   * @function
   * @returns {Array} An array of objects containing `width` and `height` for the sign variants.
   * @throws {Error} Logs an error if required `objectDataMap` or `objectVariantsMap` are undefined or invalid.
   * @throws {Warning} Logs a warning if no objects or variants are found for the specified name and version.
   *
   * @param {Object} props - The properties required to identify and extract sign object variants.
   * @param {Object} props.baselineData - Baseline data for the object.
   * @param {string} props.baselineData.objectCategory - The category of the object (e.g., "Sign").
   * @param {string} props.baselineData.objectName - The name of the selected sign object.
   * @param {string} props.baselineData.objectVersion - The version of the selected sign object.
   * @param {Object} props.objectDataMap - A mapping of object data containing all available sign objects.
   * @param {Object} props.objectVariantsMap - A mapping of object variants with their associated dimensions.
   *
   * @example
   * // Example usage:
   * const dimensions = constructObjectVariantsForSign({
   *     baselineData: {
   *         objectCategory: "Sign",
   *         objectName: "Stop",
   *         objectVersion: "v1.0"
   *     },
   *     objectDataMap: { ... },
   *     objectVariantsMap: { ... }
   * });
   */
  function constructObjectVariantsForSign() {
    const category = props?.baselineData?.objectCategory;
    const name = props?.baselineData?.objectName;
    const version = props?.baselineData?.objectVersion;

    if (!props?.objectDataMap) {
      console.error("props?.objectDataMap is undefined or null");
      return [];
    }

    // Step 1: Filter for objects with type "Sign"
    const signObjects = props.objectDataMap.filter(
      (obj: any) => obj.type === "Sign"
    );

    if (signObjects.length === 0) {
      console.error("No objects of type 'Sign' found in objectDataMap.");
      return [];
    }

    // Step 2: Find the matching sub-object where signName and latestVersion match
    let matchingObject = null;
    for (const signObject of signObjects) {

      for (const subElement of signObject.sub) {
        const currentItem = subElement[0];
        if (
          currentItem &&
          currentItem.signName === name &&
          currentItem.latestVersion === version
        ) {
          matchingObject = currentItem;
          break;
        }
      }
      if (matchingObject) {
        break;
      }
    }

    if (!matchingObject) {
      console.error(
        `No matching object found for signName '${name}' and version '${version}'.`
      );
      return [];
    }

    // Step 3: Extract signVariants and construct the list
    const signVariants = matchingObject.signVariants;
    if (!signVariants || signVariants.length === 0) {
      console.warn(
        `No signVariants found for the matching object with signName '${name}'.`
      );
      return [];
    }

    const variantList = signVariants.map((variant: any) => {
      const width = variant.width.toFixed(5);
      const height = variant.height.toFixed(5);

      if (width === undefined || height === undefined) {
        console.warn("Width or height is undefined for variant:", variant);
        return "";
      }

      const variantString = `${width} x ${height}`;
      return variantString;
    });

    // Construct variantMapping for original width/height mappings
    const variantMapping = Object.fromEntries(
      signVariants.map((variant: any) => {
        const originalWidth = variant.width;
        const originalHeight = variant.height;

        const truncatedWidth = originalWidth.toFixed(5);
        const truncatedHeight = originalHeight.toFixed(5);

        const variantString = `${truncatedWidth} x ${truncatedHeight}`;

        return [
          variantString,
          {width: originalWidth, height: originalHeight, ...variant},
        ];
      })
    );

    // Remove any empty strings from the list
    const filteredVariantList = variantList.filter((v: any) => v !== "");
    return {filteredVariantList, variantMapping}
  }

  /**
   * Memoizes the sign variants based on the provided baseline data and objectDataMap.
   *
   * @constant
   * @function
   * @returns {Object} An object containing `filteredVariantList` and `variantMapping`.
   * @throws {Warning} Logs a warning if required props (`objectCategory`, `objectName`, `objectVersion`) are missing.
   * @throws {Error} Logs an error if the result structure from `constructObjectVariantsForSign` is invalid.
   *
   * @param {Object} props - The properties required to construct sign variants.
   * @param {Object} props.baselineData - Baseline data for the object.
   * @param {string} props.baselineData.objectCategory - The category of the object.
   * @param {string} props.baselineData.objectName - The name of the object.
   * @param {string} props.baselineData.objectVersion - The version of the object.
   * @param {Object} props.objectDataMap - A mapping of object data used in construction.
   */
  const memoizedSignVariants: any = useMemo(() => {
    if (
      !props?.baselineData?.objectCategory ||
      !props?.baselineData?.objectName ||
      !props?.baselineData?.objectVersion
    ) {
      console.warn(
        "Missing required props: objectCategory, objectName, or objectVersion"
      );
      return {filteredVariantList: [], variantMapping: {}}; // Ensure this always returns an object
    }

    const result: any = constructObjectVariantsForSign() as any;

    if (!result || !result.filteredVariantList || !result.variantMapping) {
      console.error("Invalid result structure from constructObjectVariantsForSign");
      return {filteredVariantList: [], variantMapping: {}}; // Fallback
    }
    const {filteredVariantList, variantMapping} = result;
    return {filteredVariantList, variantMapping};
  }, [
    props?.baselineData?.objectCategory,
    props?.baselineData?.objectName,
    props?.baselineData?.objectVersion,
    props?.objectDataMap,
  ]);


  const [signIdToVariantMapping, setSignIdToVariantMapping] = useState<any>({})
  /**
   * React useEffect hook to manage and update the state of sign variants.
   *
   * @function
   * @returns {void}
   * @throws {Warning} Logs a warning if `memoizedSignVariants` contains invalid data.
   *
   * @param {Object} memoizedSignVariants - The memoized sign variants data.
   * @param {Array} memoizedSignVariants.filteredVariantList - The filtered list of sign variants.
   * @param {Object} memoizedSignVariants.variantMapping - The mapping of variant data.
   * @param {Function} setSignVariants - State setter for `filteredVariantList`.
   * @param {Function} setSignVariantsMapping - State setter for `variantMapping`.
   */
  useEffect(() => {
    if (
      memoizedSignVariants &&
      memoizedSignVariants.filteredVariantList &&
      memoizedSignVariants.variantMapping
    ) {
      const {filteredVariantList, variantMapping} = memoizedSignVariants as any;
      setSignVariants(filteredVariantList || []); // Fallback to empty array if undefined
      setSignVariantsToDataMapping(variantMapping || {}); // Fallback to empty object if undefined
      // get thee id froom each valiue in variantMapping and set it to signIdToVariantMapping
      let _signIdToVariantMapping: any = {}
      for (const [key, value] of Object.entries(variantMapping)) {
        const typedValue = value as any // this is to avoid typescript error
        _signIdToVariantMapping[typedValue.id] = key
      }
      setSignIdToVariantMapping(_signIdToVariantMapping)
    } else {
      console.warn("memoizedSignVariants contains invalid data:", memoizedSignVariants);
    }
  }, [memoizedSignVariants]);

  /**
   * Generates a list of distinct versions for the selected object based on its category and name.
   *
   * @function
   * @returns {Array} An array of distinct version strings.
   * @throws {Error} Logs an error if `objectDataMap` or `selectedObjectName` is invalid.
   * @throws {Warning} Logs a warning if no versions are found for the selected object.
   *
   * @param {Object} props - The properties containing baseline data and object data map.
   * @param {Object} props.baselineData - Baseline data for the object.
   * @param {string} props.baselineData.objectCategory - The category of the object (e.g., "Sign").
   * @param {string} props.baselineData.objectName - The name of the selected object.
   * @param {Object} props.objectDataMap - A mapping of object data used to extract versions.
   *
   * @example
   * // Example usage:
   * const versions = versionList({
   *     baselineData: { objectCategory: "Sign", objectName: "Stop" },
   *     objectDataMap: { ... }
   * });
   */
  function versionsList() {
    const selectedObjectCategory = props?.baselineData?.objectCategory;
    const selectedObjectName = props?.baselineData?.objectName; // Get the currently selected object name
    let latestVersions;

    // Check if objectDataMap and selectedObjectName are valid
    if (!props?.objectDataMap || !selectedObjectName) {
      console.error("objectDataMap or selectedObjectName is invalid");
      return [];
    }

    if (selectedObjectCategory == "Potentially Moving Object" || selectedObjectCategory === 'Miscellaneous') {
      // Filter for the object with type "Potentially Moving Object"
      const filteredObjects = props?.objectDataMap.filter(
        (element: any) => (element.type === "Potentially Moving Object" || element.type === "Miscellaneous") && element.type === props?.baselineData.objectCategory
      );

      // Extract versions for the selected object name only
      latestVersions = filteredObjects.flatMap((element: any) =>
        element.sub.flatMap((subArray: any[]) =>
          subArray
            .filter((item: any) => item.objectName === selectedObjectName) // Match the selected object name
            .map((item: any) => item.latestVersion)
        )
      );
    } else if (selectedObjectCategory === "Sign") {
      // Filter for the objects with type "Sign"
      const filteredObjects = props.objectDataMap?.filter(
        (element: any) => element.type === "Sign"
      );

      // Check if filteredObjects exist
      if (!filteredObjects || filteredObjects.length === 0) {
        console.error("No objects of type 'Sign' found in objectDataMap.");
        return [];
      }

      // Extract versions for the selected sign name only
      let latestVersions = filteredObjects
        ?.flatMap((subArray: any) => {
          return subArray.sub.flatMap((item: any) => {
            // Each item is an array containing a single dictionary, access the dictionary
            const currentItem = item[0];
            if (currentItem && currentItem.signName === selectedObjectName) {
              return currentItem.latestVersion;
            } else {
              return [];
            }
          });
        })
        .filter(Boolean); // Remove undefined/null values

      // Check if any versions were found
      if (!latestVersions || latestVersions.length === 0) {
        console.warn(
          `No latest versions found for signName '${selectedObjectName}'.`
        );
        return [];
      }

      // Remove duplicates from the latestVersions array
      const distinctVersions = Array.from(new Set(latestVersions));
      return distinctVersions;
    } else if (selectedObjectCategory == "Baseline") {
      // there will only be one baseline so versions do not apply
      return ["1.0"];
    }

    // Remove duplicates from the latestVersions array
    const distinctVersions = Array.from(new Set(latestVersions));
    return distinctVersions;
  }

  /**
   * Handles changes to the selected sign profile and retrieves corresponding dimensions.
   *
   * @function
   * @param {Object} e - The event object from the change handler.
   * @param {Object} e.target - The target element of the event.
   * @param {string} e.target.value - The value of the selected sign profile.
   * @returns {Object|null} An object containing `width` and `height` of the selected variant, or `null` if no mapping is found.
   * @throws {Error} Logs an error if `signVariantsMapping` is undefined or not an object.
   * @throws {Error} Logs an error if no mapping is found for the selected variant.
   *
   * @example
   * // Example usage:
   * const dimensions = handleSignProfileChange(event);
   * if (dimensions) {
   *     console.log(dimensions.width, dimensions.height);
   * }
   */
  function handleSignProfileChange(e: any) {

    // Retrieve the e.target.value
    const selectedVariant = e.target.value;

    // Check if signVariantsMapping is available
    if (!signVariantsMapping || typeof signVariantsMapping !== "object") {
      console.error("signVariantsMapping is undefined or not an object");
      return null; // Early return if mapping is invalid
    }

    // Get the width and height from the signVariantsMapping
    const variantData = signVariantsMapping[selectedVariant];

    if (!variantData) {
      console.error(
        `No mapping found for key '${selectedVariant}' in signVariantsMapping`
      );
      return null; // Early return if no mapping is found
    }

    const obj = {
      ...variantData
    }

    // Call handleProfileChange with the extracted width and height
    props?.handleProfileChange(e, obj);

    // Return the width and height
    return obj;
  }

  function helperSetValidationErrors(newVal: string, cell: any, message: string) {
    setValidationErrors({
      ...validationErrors,
      [cell.id]: message
    })

    toast.error(message,
      {
        style: {
          marginTop: "300px"
        },
        duration: 3000
      })
  }

  const validateInput = (newVal: string, cell: any) => {
    let newValnum = Number(newVal)
    if (isNaN(newValnum)) {
      helperSetValidationErrors(newVal, cell, `Not valid value, must be a number`)
      return false
    }
    if (cell.row.original.objectPropertyKey == "pixelCount" && newValnum < 1) {
      helperSetValidationErrors(newVal, cell, `Not valid value, must be greater than 0`)
      return false
    }


    return true
  }

  const [validationErrors, setValidationErrors] = useState<any>({});

  const getCommonEditTextFieldProps = (cell: MRT_Cell<ObjectPropertyRow>): MRT_ColumnDef<ObjectPropertyRow>['muiEditTextFieldProps'] => {
    return {
      error: !!validationErrors[cell.id],
      helperText: validationErrors[cell.id],
      onKeyDown: (e) => {
        if (e.code == "Tab") {
          e.stopPropagation()
          e.preventDefault()
        }
      },
      onBlur: (event) => {
        if (cell.row.original.objectPropertyKey == "orientationWidth"
          || cell.row.original.objectPropertyKey == "orientationHeight"
          || cell.row.original.objectPropertyKey == "pixelCount"
          || cell.row.original.objectPropertyKey == "pointsOnTargetWidth"
          || cell.row.original.objectPropertyKey == "pointsOnTargetHeight"
        ) {
          const isValid = validateInput(event.target.value, cell);
          if (isValid) {
            delete validationErrors[cell.id];
            const newValnum = Number(event.target.value)
            if (cell.row.original.objectPropertyKey == "orientationWidth") {
              props?.calcHorizontalSize(newValnum, props?.baselineData.orientationHeight, props?.baselineData.selectDimension)
            } else if (cell.row.original.objectPropertyKey == "orientationHeight") {
              props?.calcVerticalSize(props?.baselineData.orientationWidth, newValnum, props?.baselineData.selectDimension)
            } else if (cell.row.original.objectPropertyKey == "pixelCount") {
              props?.calcPixelCount(newValnum)
            } else if (cell.row.original.objectPropertyKey == "pointsOnTargetWidth") {
              props?.calcPointsOnTarget({pointsOnTargetWidth: newValnum})
            } else if (cell.row.original.objectPropertyKey == "pointsOnTargetHeight") {
              props?.calcPointsOnTarget({pointsOnTargetHeight: newValnum})
            }
          }
        }
      },
    };
  }

  const EDITABLE_FIELDS = ['pixelCount', 'pointsOnTargetWidth', 'pointsOnTargetHeight', 'orientationWidth', 'orientationHeight'];

  const objectPropertiesRowColumns : MRT_ColumnDef<ObjectPropertyRow>[] = [
    {
      header: "Name",
      accessorKey: "objectPropertyName",
      enableEditing: false,
    },
    {
      header: "Value",
      accessorKey: "objectPropertyValue",
      enableEditing: true,
      minSize: 700,
      muiEditTextFieldProps: ({cell}) => ({
        ...getCommonEditTextFieldProps(cell),
        disabled: !EDITABLE_FIELDS.includes(cell.row.original.objectPropertyKey),
      }),
      Cell: ({cell}) => {
        let _objectPropertyKey: string = cell.row.original.objectPropertyKey
        let inputObject: any;
        objectPropertiesInput.forEach((input: ObjectPropertiesInput) => {
          if (input.key == _objectPropertyKey) {
            inputObject = input
          }
        })

        if (inputObject.isDropDown) {
          let dropdownValue: any;
          let menuItems: any = []
          let variants: any = []
          switch (_objectPropertyKey) {
            case "objectCategory":
              dropdownValue = props?.baselineData?.objectCategory
              menuItems = props?.objectDataMap
                .filter((element: any) => element.type !== 'Custom')
                .map((element: any) => element.type);
              break;
            case "objectName":
              if (props?.baselineData?.objectCategory != "Custom") {
                if (props?.baselineData?.objectCategory == "Potentially Moving Object" || props.baselineData.objectCategory === 'Miscellaneous') {
                  // Filter for the 'Potentially Moving Object' type
                  const filteredObjects = Object.values(props?.objectDataMap || {}).filter(
                    (element: any) => (element.type === "Potentially Moving Object" || element.type === "Miscellaneous") && element.type === props?.baselineData.objectCategory
                  );
                  // Extract all object names from 'sub' array of arrays
                  menuItems = filteredObjects.flatMap((element: any) =>
                    element.sub.flatMap((innerArray: any[]) =>
                      innerArray.map((item: any) => item.objectName)
                    )
                  );
                } else if (props?.baselineData?.objectCategory == "Baseline") {
                  menuItems = (Object.values(props?.objectDataMap.filter((element: any) => element.type == props?.baselineData.objectCategory)[0]
                    .sub.map((element: any) => props?.processListOfVersions(element)))).map((element: any) => (Object.keys(element))[0])
                } else if (props?.baselineData?.objectCategory == "Sign") {
                  const filteredObjects = Object.values(props?.objectDataMap || {}).filter(
                    (element: any) => element.type === "Sign"
                  );
                  // Extract all object names from 'sub' array of arrays
                  menuItems = filteredObjects.flatMap((element: any) =>
                    element.sub.flatMap((innerArray: any[]) =>
                      innerArray.map((item: any) => item.signName)
                    )
                  );
                }
              } else {
                menuItems = []
              }
              dropdownValue = props?.baselineData?.objectName
              break;
            case "objectVersion":
              dropdownValue = props?.baselineData?.objectVersion
              if (props?.baselineData?.objectName != "" && props?.baselineData?.objectCategory != "Custom") {
                menuItems = versionsList()
              } else {
                menuItems = []
              }
              break;
            case "objectProperties":
              if (props?.baselineData?.objectCategory == "Baseline") {
                dropdownValue = props?.baselineData?.objectVariant;
                menuItems = ["Front Profile", "Side Profile", "Rear Profile"]
              } else if (props?.baselineData?.objectCategory == "Potentially Moving Object" || props.baselineData.objectCategory === 'Miscellaneous') {
                dropdownValue = props?.baselineData?.objectVariant;
                menuItems = ["Front Profile", "Side Profile", "Rear Profile"]
              } else if (props?.baselineData?.objectCategory == "Sign") {
                dropdownValue = props?.baselineData?.objectVariant;
                const variantId = props.baselineData?.objectId
                dropdownValue = signIdToVariantMapping[variantId]
                menuItems = signVariants
              } else {
                dropdownValue = "";
                menuItems = []
              }
              break;
            case "selectDimension":
              dropdownValue = props?.baselineData?.selectDimension
              menuItems = dimensionOptions;
              break;
          }
          const isBaseline = props?.baselineData?.objectCategory == "Baseline"
          const shouldDisable = isBaseline && (_objectPropertyKey === 'objectName' || _objectPropertyKey === 'objectVersion' || _objectPropertyKey === 'objectProperties')
          return (
            <ArgosSelect
              defaultValue={_objectPropertyKey == "selectDimension" && menuItems[0]}
              disabled={shouldDisable || (cell.row.original.objectPropertyKey == "objectVersion" && (props?.baselineData?.objectName == "" || props?.baselineData?.objectCategory == "Custom"))
                || (cell.row.original.objectPropertyKey == "objectName" && props?.baselineData?.objectCategory == "Custom")}
              value={dropdownValue ?? ""} // This can't be undefined otherwise it will break component (it will be stuck blank)
              onChange={(e: any) => {
                if (cell.row.original.objectPropertyKey == "objectCategory") {
                  props?.handleObjectCategoryChange(e)
                } else if (cell.row.original.objectPropertyKey == "objectName") {
                  props?.handleObjectNameChange(e)
                } else if (cell.row.original.objectPropertyKey == "objectVersion") {
                  props?.handleObjectVersionChange(e)
                } else if (cell.row.original.objectPropertyKey == "objectProperties") {
                  if (props?.baselineData?.objectCategory == "Sign") {
                    handleSignProfileChange(e)
                  } else {
                    props?.handleProfileChange(e)
                  }
                } else {
                  props?.handleDimensionChange(e)
                }
              }}
            >
              {menuItems.map((item: any) => <MenuItem value={String(item)}>{item} </MenuItem>)}
            </ArgosSelect>)
        } else {
          return <div> {cell.getValue() as any} </div>
        }
      }
    },
    {
      header: "Unit",
      accessorKey: "unit",
      enableEditing: false,
    },
  ]
  return (
    <div style={{padding: "5px"}}>
      <ArgosTableSimple
        columns={objectPropertiesRowColumns}
        data={tableData}
        editDisplayMode="cell"
        enableEditing
        enableBottomToolbar={false}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
        initialState={{density: 'compact'}}
      />
    </div>)
}
export default ObjectProperties;